/**
 * sharedLaunchProfile — Cookie compartida entre apps de Habi.
 *
 * Permite pasar el `launch_profile` (email + name) del launcher
 * (Portal Pricing) a otras apps como Cortex sin depender de localStorage
 * (que NO se comparte entre subdominios). La cookie se escribe con
 * `Domain=.<TLD>` y queda accesible desde cualquier subdominio del mismo TLD/SLD.
 *
 *   Portal Pricing launcher    →  pricing.llavero.co        (escribe)
 *   Cortex (transitorio)       →  *.im.private.llavero.co   (lee)
 *   Cortex (estable)           →  cortex.llavero.co         (lee)
 *   PROD                       →  *.habi.co                 (rootDomain detecta)
 *
 * En local (`localhost` / `127.0.0.1`) no se setea `Domain` y queda como
 * cookie host-only — sin riesgo de pisar entre apps en el mismo puerto.
 */

export interface LaunchProfile {
  email:     string;
  name?:     string;
  lastName?: string;
}

const COOKIE_NAME     = 'habi_launch_profile';
const MAX_AGE_SECONDS = 12 * 60 * 60;  // 12h — match con el TTL del token de Google

/**
 * Devuelve el dominio "padre" para compartir cookies entre apps:
 *   `*.llavero.co` (cualquier profundidad) → `.llavero.co`
 *   `*.habi.co`    (cualquier profundidad) → `.habi.co`
 *   `localhost` / `127.0.0.1`              → undefined (cookie host-only)
 *
 * Implementación: toma los últimos 2 segments del hostname. Funciona con
 * `cortex.llavero.co` (estable) y `cortex-front-domain-cortex-dev.im.private.llavero.co`
 * (transitorio) sin cambiar código.
 *
 * Exportado (con `host` opcional) para ser testeable sin tocar window.location.
 */
export function rootDomain(host?: string): string | undefined {
  const h = host ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  if (!h || h === 'localhost' || h === '127.0.0.1') return undefined;
  const parts = h.split('.');
  return parts.length >= 2 ? '.' + parts.slice(-2).join('.') : undefined;
}

// El JSON puede contener caracteres no-ASCII (ej. "Cañón"). `btoa` solo
// acepta latin-1, así que codificamos primero a UTF-8 bytes con TextEncoder
// y los pasamos a `btoa` como char codes.
function encodeUtf8Base64(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}
function decodeUtf8Base64(s: string): string {
  const binary = atob(s);
  const bytes  = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

/** Escribe el profile como cookie compartida. No-op si no hay `document`. */
export function writeLaunchProfile(profile: LaunchProfile): void {
  if (typeof document === 'undefined') return;
  const domain = rootDomain();
  const value  = encodeUtf8Base64(JSON.stringify(profile));
  // `Secure` solo cuando hay dominio compartido (i.e. ambientes HTTPS reales).
  // En localhost (HTTP) `Secure` haría que el browser ignore la cookie.
  const attrs = [
    `${COOKIE_NAME}=${value}`,
    'Path=/',
    'SameSite=Lax',
    `Max-Age=${MAX_AGE_SECONDS}`,
    domain ? `Domain=${domain}` : '',
    domain ? 'Secure'           : '',
  ].filter(Boolean).join('; ');
  document.cookie = attrs;
}

/** Lee el profile de la cookie. Retorna `null` si no existe o está corrupta. */
export function readLaunchProfile(): LaunchProfile | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]+)`));
  if (!match) return null;
  try {
    const parsed = JSON.parse(decodeUtf8Base64(match[1])) as Partial<LaunchProfile>;
    if (typeof parsed.email !== 'string' || parsed.email.length === 0) return null;
    return {
      email:    parsed.email,
      name:     typeof parsed.name     === 'string' ? parsed.name     : undefined,
      lastName: typeof parsed.lastName === 'string' ? parsed.lastName : undefined,
    };
  } catch {
    return null;
  }
}

/** Borra la cookie. Útil en logout. */
export function clearLaunchProfile(): void {
  if (typeof document === 'undefined') return;
  const domain = rootDomain();
  document.cookie = [
    `${COOKIE_NAME}=`,
    'Path=/',
    'Max-Age=0',
    'SameSite=Lax',
    domain ? `Domain=${domain}` : '',
    domain ? 'Secure'           : '',
  ].filter(Boolean).join('; ');
}
